import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import client, { formatApiError, fmtTime, fmtDay } from "../lib/api";

const fmtSince = (iso) => (iso && new Date(iso).toDateString() !== new Date().toDateString() ? `${fmtDay(iso)}, ${fmtTime(iso)}` : fmtTime(iso));
import { useAuth } from "../lib/auth";

const SHOP_KEY = "shop_token";
const shopHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(SHOP_KEY)}` } });

export default function Kiosk() {
  const nav = useNavigate();
  const { brand, wallpaper } = useAuth();
  const [open, setOpen] = useState(!!localStorage.getItem(SHOP_KEY));
  const wpUrl = wallpaper?.url || "";

  const closeLocal = () => { localStorage.removeItem(SHOP_KEY); setOpen(false); };

  return (
    <div className="min-h-screen bg-app text-ivory font-body relative overflow-hidden">
      {wpUrl && (
        <>
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${wpUrl})` }} data-testid="kiosk-wallpaper" />
          <div className="absolute inset-0 bg-white/75" />
        </>
      )}
      <div className="max-w-[720px] mx-auto px-5 pb-16 relative">
        <Header brand={brand} onOwner={() => nav("/owner-access")} />
        {open
          ? <ShopFloor onClosed={closeLocal} />
          : <OpenShop onOpened={() => setOpen(true)} />}
      </div>
    </div>
  );
}

function Header({ brand, onOwner }) {
  return (
    <div className="flex items-center justify-between pt-6 pb-2">
      <div>
        <div className="font-serif text-[26px] font-semibold text-ivory tracking-wide leading-tight" data-testid="brand-title">
          {brand?.title || "Register"}
        </div>
        {brand?.subtitle && <div className="caps-lg text-gold mt-1">{brand.subtitle}</div>}
      </div>
      <button
        onClick={onOwner}
        data-testid="header-owner-btn"
        className="rounded-full border border-line text-muted-jewel px-4 py-2 text-sm hover:text-ivory hover:border-gold-deep transition-colors"
      >
        Owner panel
      </button>
    </div>
  );
}

function PinInput({ value, onChange, onEnter, testid, autoFocus }) {
  return (
    <input
      type="password"
      inputMode="numeric"
      value={value}
      autoFocus={autoFocus}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 12))}
      onKeyDown={(e) => e.key === "Enter" && onEnter && onEnter()}
      placeholder="Owner PIN"
      data-testid={testid}
      className="w-full text-center text-2xl tracking-[0.35em] py-3 bg-panel-soft text-ivory border border-line rounded-lg focus:outline-none"
    />
  );
}

function OpenShop({ onOpened }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (pin.length < 4) return;
    setBusy(true);
    try {
      const { data } = await client.post("/shop/open", { pin });
      localStorage.setItem(SHOP_KEY, data.token);
      toast.success("Shop is open");
      onOpened();
    } catch (e) {
      toast.error(formatApiError(e));
      setPin("");
    } finally {
      setBusy(false);
    }
  };
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="pt-14 max-w-[420px]" data-testid="open-shop-screen">
      <h1 className="font-serif text-4xl sm:text-5xl font-medium mb-2">Shop is closed</h1>
      <p className="text-muted-jewel text-sm mb-7">Owner: enter your PIN to open the shop for today. Staff can then punch in and out here.</p>
      <PinInput value={pin} onChange={setPin} onEnter={submit} testid="open-shop-pin" autoFocus />
      <button
        onClick={submit}
        disabled={busy || pin.length < 4}
        data-testid="open-shop-btn"
        className="seal-primary w-full mt-4 rounded-xl py-4 text-lg font-semibold disabled:opacity-40"
      >
        {busy ? "Opening…" : "Open shop"}
      </button>
    </motion.div>
  );
}

function ShopFloor({ onClosed }) {
  const [staff, setStaff] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [picked, setPicked] = useState(null);
  const [closing, setClosing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await client.get("/shop/staff", shopHeaders());
      setStaff(data);
      setLoaded(true);
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("The shop session ended — please open the shop again"); onClosed(); }
    }
  }, [onClosed]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const inCount = staff.filter((s) => s.state === "in").length;

  return (
    <div className="pt-8" data-testid="shop-floor">
      <div className="flex items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-serif text-4xl sm:text-5xl font-medium">Tap your name</h1>
          <p className="text-muted-jewel text-sm mt-1" data-testid="shop-in-count">{inCount} of {staff.length} at work now</p>
        </div>
        <button
          onClick={() => setClosing(true)}
          data-testid="close-shop-btn"
          className="shrink-0 rounded-full border border-line text-muted-jewel px-4 py-2 text-sm hover:text-ivory hover:border-gold-deep transition-colors"
        >
          Close shop
        </button>
      </div>
      {loaded && staff.length === 0 && (
        <div className="bg-panel/80 border border-dashed border-line rounded-2xl p-6 text-muted-jewel text-sm" data-testid="staff-empty">
          No staff yet. The owner can add names in the Owner panel → Staff.
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {staff.map((s, i) => (
          <NameTile key={s.id} s={s} i={i} onPick={() => setPicked(s)} />
        ))}
      </div>
      <AnimatePresence>
        {picked && <ConfirmPunch s={picked} onDone={() => { setPicked(null); load(); }} onCancel={() => setPicked(null)} />}
      </AnimatePresence>
      {closing && <CloseShop staff={staff} onCancel={() => setClosing(false)} onClosed={onClosed} />}
    </div>
  );
}

function NameTile({ s, i, onPick }) {
  const isIn = s.state === "in";
  return (
    <motion.button
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(i * 0.03, 0.3) }}
      onClick={onPick}
      data-testid={`staff-tile-${s.id}`}
      className={`text-left rounded-2xl border px-4 py-5 bg-panel/90 backdrop-blur transition-colors active:scale-[0.98] ${isIn ? "" : "border-line hover:border-gold-deep"}`}
      style={isIn ? { borderColor: "#3f8f52" } : undefined}
    >
      <div className="font-serif text-xl font-semibold text-ivory leading-tight break-words">{s.name}</div>
      <div className={`text-[12px] mt-1.5 inline-flex items-center gap-1.5 ${isIn ? "text-jewel-green" : "text-muted-jewel"}`}>
        <span className="w-2 h-2 rounded-full" style={{ background: isIn ? "#3f8f52" : "#b9ad9a" }} />
        {isIn ? `In since ${fmtSince(s.since)}` : "Not in"}
      </div>
    </motion.button>
  );
}

function ConfirmPunch({ s, onDone, onCancel }) {
  const [busy, setBusy] = useState(false);
  const type = s.state === "in" ? "out" : "in";
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await client.post("/shop/punch", { employee_id: s.id, type }, shopHeaders());
      toast.success(`${s.name} punched ${type} at ${fmtTime(data.event.ts)}`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
      onDone();
    }
  };
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3"
      onClick={onCancel}
      data-testid="confirm-punch-backdrop"
    >
      <motion.div
        initial={{ y: 24 }} animate={{ y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-panel border border-line rounded-2xl w-full max-w-[440px] p-6"
        data-testid="confirm-punch-modal"
      >
        <div className="caps text-muted-jewel mb-1">{type === "in" ? "Punch in" : "Punch out"}</div>
        <div className="font-serif text-3xl text-ivory mb-1" data-testid="confirm-punch-name">Are you {s.name}?</div>
        <p className="text-muted-jewel text-sm mb-6">
          {type === "in" ? "This records your start time as now." : `You've been in since ${fmtSince(s.since)}. This records your finish time as now.`}
        </p>
        <button
          onClick={go}
          disabled={busy}
          data-testid="confirm-punch-yes"
          className="seal-primary w-full rounded-xl py-4 text-lg font-semibold disabled:opacity-40"
        >
          Yes, it's me — punch {type}
        </button>
        <button
          onClick={onCancel}
          data-testid="confirm-punch-no"
          className="w-full mt-2.5 rounded-xl border border-line text-muted-jewel py-3 text-[15px] hover:text-ivory transition-colors"
        >
          Not me
        </button>
      </motion.div>
    </motion.div>
  );
}

function CloseShop({ staff, onCancel, onClosed }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const stillIn = staff.filter((s) => s.state === "in");
  const close = async (punchAllOut) => {
    if (pin.length < 4) { toast.error("Enter the owner PIN"); return; }
    setBusy(true);
    try {
      const { data } = await client.post("/shop/close", { pin, punch_all_out: punchAllOut }, shopHeaders());
      toast.success(data.punched_out.length ? `Shop closed — punched out ${data.punched_out.length}` : "Shop closed");
      if (data.skipped_old?.length) toast.warning(`Left open (in for over 16h): ${data.skipped_old.join(", ")}. Fix their punch-out in the Owner panel.`, { duration: 10000 });
      onClosed();
    } catch (e) {
      toast.error(formatApiError(e));
      setPin("");
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={onCancel} data-testid="close-shop-backdrop">
      <div onClick={(e) => e.stopPropagation()} className="bg-panel border border-line rounded-2xl w-full max-w-[440px] p-6" data-testid="close-shop-modal">
        <div className="font-serif text-3xl text-ivory mb-2">Close the shop</div>
        {stillIn.length > 0 ? (
          <div className="rounded-xl border border-jewel-amber bg-panel-soft p-3 mb-4" data-testid="close-shop-still-in">
            <div className="caps text-jewel-amber text-[11px] mb-1.5">{stillIn.length} still punched in</div>
            {stillIn.map((s) => (
              <div key={s.id} className="flex justify-between text-[14px] py-1" data-testid={`still-in-${s.id}`}>
                <span className="text-ivory">{s.name}</span>
                <span className="text-muted-jewel">since {fmtSince(s.since)}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-jewel text-sm mb-4" data-testid="close-shop-all-out">Everyone has punched out.</p>
        )}
        <PinInput value={pin} onChange={setPin} testid="close-shop-pin" autoFocus />
        {stillIn.length > 0 ? (
          <>
            <button onClick={() => close(true)} disabled={busy} data-testid="close-shop-punch-all" className="seal-primary w-full mt-4 rounded-xl py-4 text-[16px] font-semibold disabled:opacity-40">
              Punch everyone out & close
            </button>
            <button onClick={() => close(false)} disabled={busy} data-testid="close-shop-leave-open" className="w-full mt-2.5 rounded-xl border border-line text-muted-jewel py-3 text-[14px] hover:text-ivory transition-colors">
              Close without punching them out
            </button>
          </>
        ) : (
          <button onClick={() => close(false)} disabled={busy} data-testid="close-shop-confirm" className="seal-primary w-full mt-4 rounded-xl py-4 text-[16px] font-semibold disabled:opacity-40">
            Close shop
          </button>
        )}
        <button onClick={onCancel} data-testid="close-shop-cancel" className="w-full mt-2.5 text-muted-jewel text-sm py-2 hover:text-ivory transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
}
